import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sprout, Flame, Crown, Sparkles, MousePointerClick, Wallet, Heart, MailWarning, Loader2, Copy, Megaphone, GraduationCap, LinkIcon, Eye } from "lucide-react";
import { authAxios, useAuth } from "@/context/AuthContext";
import { ProgramCard } from "@/components/ProgramCard";
import { DashboardSocials } from "@/components/DashboardSocials";
import { DashboardSignups } from "@/components/DashboardSignups";
import { AdultOptInCard } from "@/components/AdultOptInCard";

const TIER_ICONS = { Noobie: Sprout, Rising: Flame, Elite: Crown, Superstar: Sparkles };

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [myLinks, setMyLinks] = useState([]);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    authAxios.get("/user/dashboard").then((r) => setData(r.data)).catch(console.error);
    authAxios.get("/user/links").then((r) => setMyLinks(r.data.links || [])).catch(() => {});
  }, []);

  const resendVerification = async () => {
    setResending(true);
    try {
      const r = await authAxios.post("/auth/resend-verification", { origin_url: window.location.origin });
      toast.success(r.data.status === "already_verified" ? "Already verified!" : "Verification email sent — check your inbox.");
    } catch {
      toast.error("Could not send verification email.");
    } finally {
      setResending(false);
    }
  };

  if (!data) return <main className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-400">Loading your dashboard…</main>;

  const { tier, stats, favorites } = data;
  const TierIcon = TIER_ICONS[tier.name] || Sprout;

  return (
    <main data-testid="dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2">Affiliate Dashboard</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-8">Hey, {data.user.name.split(" ")[0]}.</h1>

      {!data.user.email_verified && (
        <div data-testid="verify-email-banner" className="flex flex-col sm:flex-row sm:items-center gap-4 bg-amber-50 border border-amber-200 rounded-lg p-5 mb-10">
          <MailWarning className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 flex-1">Your email isn't verified yet. Verify to unlock your $25 sign-up ad credits.</p>
          <button onClick={resendVerification} disabled={resending} data-testid="resend-verification-button"
            className="flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-amber-600 text-white text-sm font-medium hover:bg-amber-500 transition-colors duration-200 disabled:opacity-60">
            {resending && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Resend email
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-14">
        <div data-testid="tier-progress-card" className="lg:col-span-2 relative bg-slate-900 rounded-xl p-8 overflow-hidden">
          <div className="noise-overlay" />
          <div className="relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-white/10 border border-white/15 rounded-lg p-2.5"><TierIcon className="w-6 h-6 text-emerald-400" /></div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Current tier</p>
                  <p className="font-heading text-2xl text-white font-semibold">{tier.name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-heading text-3xl text-emerald-400 font-medium">{tier.commission}%</p>
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">commission</p>
              </div>
            </div>
            {tier.next_tier ? (
              <>
                <div className="flex justify-between text-xs text-slate-400 mb-2">
                  <span>{stats.clicks_this_month.toLocaleString()} clicks this month</span>
                  <span>{tier.next_threshold.toLocaleString()} for {tier.next_tier}</span>
                </div>
                <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-[width] duration-500" style={{ width: `${tier.progress_pct}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-3">{tier.progress_pct}% of the way to <span className="text-emerald-400 font-semibold">{tier.next_tier}</span></p>
              </>
            ) : (
              <p className="text-sm text-emerald-400 font-medium">You're at the top. Superstar status unlocked. 🌟</p>
            )}
          </div>
        </div>
        <div className="grid grid-rows-3 gap-4">
          {[
            [MousePointerClick, "Clicks this month", stats.clicks_this_month.toLocaleString(), "dashboard-stat-clicks"],
            [Wallet, "Total earned", `$${stats.total_earned.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, "dashboard-stat-earned"],
            [Heart, "Saved programs", stats.favorites_count, "dashboard-stat-favorites"],
          ].map(([Icon, label, value, tid]) => (
            <div key={label} className="flex items-center gap-4 bg-white border border-slate-200 rounded-lg px-6">
              <Icon className="w-5 h-5 text-emerald-600" />
              <div>
                <p data-testid={tid} className="font-heading text-xl font-medium">{value}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Share link + growth tools */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-14">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-2 flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5" /> Your ambassador link</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <code data-testid="dashboard-share-link" className="flex-1 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 break-all">
              {`${window.location.origin}/go/${data.user.handle || ""}`}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/go/${data.user.handle}`).then(() => toast.success("Link copied — share it anywhere!"))}
              data-testid="dashboard-copy-link-button"
              className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors duration-200">
              <Copy className="w-4 h-4" /> Copy
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-3">Every click through this link is tracked to you with a 30-day attribution window. Unpaid balance: <span className="font-semibold text-slate-900">${(stats.balance ?? 0).toFixed(2)}</span></p>
          {myLinks.length > 0 && (
            <div data-testid="dashboard-link-products" className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-100">
              {myLinks.map((l) => (
                <div key={l.id} data-testid={`dashboard-link-product-${l.slug}`} className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-full pl-1.5 pr-4 py-1.5">
                  {l.logo_path ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/link-logo/${l.id}`} alt={`${l.name} logo`} className="w-7 h-7 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center"><LinkIcon className="w-3.5 h-3.5 text-emerald-700" /></div>
                  )}
                  <span className="text-xs font-medium">{l.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Link to="/ads" data-testid="dashboard-ads-link" className="card-lift flex flex-col justify-center bg-slate-900 rounded-lg p-5">
            <Megaphone className="w-6 h-6 text-emerald-400 mb-2" />
            <p className="font-heading font-semibold text-white text-sm">Ad Library</p>
            <p className="text-xs text-slate-400">Copy-paste ads</p>
          </Link>
          <Link to="/tutorials" data-testid="dashboard-tutorials-link" className="card-lift flex flex-col justify-center bg-white border border-slate-200 rounded-lg p-5">
            <GraduationCap className="w-6 h-6 text-emerald-600 mb-2" />
            <p className="font-heading font-semibold text-sm">Tutorials</p>
            <p className="text-xs text-slate-500">Marketing tactics</p>
          </Link>
        </div>
      </div>

      <div className="flex items-end justify-between mb-6">
        <h2 className="font-heading text-2xl md:text-3xl tracking-tight">Your saved programs</h2>
        <Link to="/programs" data-testid="dashboard-browse-link" className="text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors duration-200">Browse directory →</Link>
      </div>
      {favorites.length === 0 ? (
        <div data-testid="favorites-empty-state" className="bg-white border border-dashed border-slate-300 rounded-lg p-14 text-center">
          <Heart className="w-8 h-8 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-sm mb-5">No saved programs yet. Tap the heart on any program to save it here.</p>
          <Link to="/programs" className="inline-block px-7 py-3 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 transition-colors duration-200">Find programs</Link>
        </div>
      ) : (
        <div data-testid="favorites-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favorites.map((p) => <ProgramCard key={p.slug} program={p} />)}
        </div>
      )}

      {/* Personal-account extras: socials, signups, adult opt-in */}
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DashboardSocials />
        <DashboardSignups />
      </div>
      <div className="mt-6">
        <AdultOptInCard />
      </div>
    </main>
  );
}
